import type { ProviderDetail } from '../../../../../js/api';
import { formatEin } from '../../../../../js/utils/string';
import {
  EMPTY_VALUE,
  formatProviderDate,
  labelFor,
  labelsFor,
  ProviderDrawerMaps,
  textOrDash,
  yesNo,
} from './providerDisplay';
import { FieldSection, FieldSpec } from './ProviderDrawerPrimitives';

/** "Ada (Addie)" when the preferred name differs from the first name, else just the first name. */
const formatFirstName = (firstName: string, preferredName: string): string => {
  const first = textOrDash(firstName);
  const preferred = preferredName.trim();
  return preferred && preferred !== firstName.trim() ? `${first} (${preferred})` : first;
};

/** Basic info, contact, address, birth location and military — the old "Credentials" tab. */
export function CredentialsTab({
  provider,
  maps,
}: {
  provider: ProviderDetail;
  maps: ProviderDrawerMaps;
}) {
  const basicFields: FieldSpec[] = [
    { label: 'Prefix', value: textOrDash(provider.prefix_name) },
    { label: 'First Name', value: formatFirstName(provider.first_name, provider.preferred_name) },
    { label: 'Middle Name', value: textOrDash(provider.middle_name) },
    { label: 'Last Name', value: textOrDash(provider.last_name) },
    { label: 'Credential', value: textOrDash(provider.credential_name) },
    { label: 'Other Names', value: textOrDash(provider.other_names) },
    { label: 'Degree(s)', value: labelsFor(provider.degree, maps.Degree) },
    { label: 'NPI', value: textOrDash(provider.npi) },
    { label: 'Birthday', value: formatProviderDate(provider.birth_date) },
    { label: 'Gender', value: labelFor(provider.gender, maps.Gender) },
    { label: 'EIN', value: provider.ein ? formatEin(provider.ein) : EMPTY_VALUE },
    { label: 'Business Name', value: textOrDash(provider.business_name) },
  ];

  const contactFields: FieldSpec[] = [
    {
      label: 'Email',
      value: provider.email ? (
        <a href={`mailto:${provider.email}`} className="tw:text-brand-green-0 tw:hover:underline">
          {provider.email}
        </a>
      ) : (
        EMPTY_VALUE
      ),
    },
    { label: 'Phone', value: textOrDash(provider.phone_number) },
    { label: 'Alt Phone', value: textOrDash(provider.phone_number_alternative) },
    { label: 'Texting', value: provider.texting_number || 'Opted out' },
  ];

  const addressFields: FieldSpec[] = [
    {
      label: 'Street',
      value: provider.street_address2 ? (
        <span className="tw:flex tw:flex-col">
          <span>{textOrDash(provider.street_address)}</span>
          <span>{provider.street_address2}</span>
        </span>
      ) : (
        textOrDash(provider.street_address)
      ),
    },
    {
      label: 'City, State',
      value: `${textOrDash(provider.city)}, ${textOrDash(provider.state)}`,
    },
    { label: 'Zip Code', value: textOrDash(provider.zip_code) },
  ];

  const birthFields: FieldSpec[] = [
    { label: 'City', value: textOrDash(provider.birth_city) },
    { label: 'State', value: textOrDash(provider.birth_state) },
    { label: 'Country', value: textOrDash(provider.birth_country) },
  ];

  const militaryFields: FieldSpec[] = [
    { label: 'Status', value: labelFor(provider.military_status, maps.MilitaryStatus) },
    { label: 'Branch', value: labelFor(provider.military_branch, maps.MilitaryBranch) },
    { label: 'DD214 on file', value: yesNo(provider.has_dd214) },
  ];

  return (
    <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-x-3 tw:gap-y-4">
      <FieldSection title="Basic Info" fields={basicFields} data-testid="provider-basic-info" />
      <div className="tw:flex tw:flex-col tw:gap-4">
        <FieldSection title="Contact Info" fields={contactFields} />
        <FieldSection title="Address" fields={addressFields} />
        <FieldSection title="Birth Location" fields={birthFields} />
        <FieldSection title="Military" fields={militaryFields} />
      </div>
    </div>
  );
}
